/**
 * Lasso Geometry Utility - Screen-Space Lasso Selection
 *
 * Handles all geometric calculations for screen-space lasso selection:
 * - Screen/world coordinate conversion
 * - Point-in-polygon testing
 * - Camera state capture/restore
 * - Ray casting from camera
 *
 * Based on THREE.js coordinate systems and projection matrices.
 */

/* eslint-disable no-unused-vars */
const THREE = window.THREE;

/**
 * Convert screen coordinates to normalized device coordinates (NDC)
 * NDC range: x and y from -1 to +1
 *
 * @param {number} x - Screen X coordinate (pixels from left)
 * @param {number} y - Screen Y coordinate (pixels from top)
 * @param {HTMLElement} canvas - Renderer canvas element
 * @returns {{x: number, y: number}} NDC coordinates
 */
export function screenToNDC(x, y, canvas) {
  const rect = canvas.getBoundingClientRect();

  return {
    x: ((x - rect.left) / rect.width) * 2 - 1,
    y: -((y - rect.top) / rect.height) * 2 + 1  // Flip Y axis
  };
}

/**
 * Convert normalized device coordinates (NDC) to screen coordinates
 * Inverse of screenToNDC - used for visualization when lasso is stored in NDC
 *
 * @param {number} ndcX - NDC X coordinate (-1 to 1)
 * @param {number} ndcY - NDC Y coordinate (-1 to 1)
 * @param {HTMLElement} canvas - Renderer canvas element
 * @returns {{x: number, y: number}} Screen coordinates (pixels)
 */
export function ndcToScreen(ndcX, ndcY, canvas) {
  const rect = canvas.getBoundingClientRect();

  return {
    x: ((ndcX + 1) / 2) * rect.width + rect.left,
    y: ((1 - ndcY) / 2) * rect.height + rect.top
  };
}

/**
 * Project 3D world point to 2D screen coordinates
 * Includes depth value for frustum culling
 *
 * @param {THREE.Vector3} worldPos - World position
 * @param {THREE.Camera} camera - Active camera
 * @param {HTMLElement} canvas - Renderer canvas element
 * @returns {{x: number, y: number, depth: number}} Screen coords + normalized depth
 */
export function worldToScreen(worldPos, camera, canvas) {
  const vector = worldPos.clone();
  vector.project(camera);

  const rect = canvas.getBoundingClientRect();

  return {
    x: (vector.x * 0.5 + 0.5) * rect.width,
    y: (-(vector.y * 0.5 - 0.5)) * rect.height,
    depth: vector.z  // Normalized depth (-1 = near, 1 = far)
  };
}

/**
 * Create ray from camera through screen point
 * Used for raycasting and frustum testing
 *
 * @param {number} x - Screen X coordinate (pixels)
 * @param {number} y - Screen Y coordinate (pixels)
 * @param {THREE.Camera} camera - Active camera
 * @param {HTMLElement} canvas - Renderer canvas element
 * @returns {THREE.Ray} Ray from camera origin through screen point
 */
export function screenToRay(x, y, camera, canvas) {
  const ndc = screenToNDC(x, y, canvas);
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(ndc, camera);
  return raycaster.ray;
}

/**
 * Point-in-polygon test using ray casting algorithm
 * Works for any simple polygon (convex or concave)
 *
 * Algorithm: Cast ray from point to infinity, count intersections with edges.
 * Odd count = inside, even count = outside.
 *
 * @param {{x: number, y: number}} point - Test point
 * @param {Array<{x: number, y: number}>} polygon - Polygon vertices
 * @returns {boolean} True if point is inside polygon
 */
export function isPointInPolygon(point, polygon) {
  if (!point || !polygon || polygon.length < 3) {
    return false;
  }

  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    // Check if ray crosses edge
    const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Calculate 2D bounding box for polygon
 * Used for fast pre-filtering before point-in-polygon test
 *
 * @param {Array<{x: number, y: number}>} polygon - Polygon vertices
 * @returns {{min: {x, y}, max: {x, y}}} Axis-aligned bounding box
 */
export function calculatePolygonBoundingBox(polygon) {
  if (!polygon || polygon.length === 0) {
    return { min: { x: 0, y: 0 }, max: { x: 0, y: 0 } };
  }

  const bbox = {
    min: { x: Infinity, y: Infinity },
    max: { x: -Infinity, y: -Infinity }
  };

  for (const point of polygon) {
    bbox.min.x = Math.min(bbox.min.x, point.x);
    bbox.min.y = Math.min(bbox.min.y, point.y);
    bbox.max.x = Math.max(bbox.max.x, point.x);
    bbox.max.y = Math.max(bbox.max.y, point.y);
  }

  return bbox;
}

/**
 * Test if point is inside bounding box
 * Fast pre-filter before expensive point-in-polygon test
 *
 * @param {{x: number, y: number}} point - Test point
 * @param {{min: {x, y}, max: {x, y}}} bbox - Bounding box
 * @returns {boolean} True if point inside bbox
 */
export function isInBoundingBox(point, bbox) {
  return point.x >= bbox.min.x
      && point.x <= bbox.max.x
      && point.y >= bbox.min.y
      && point.y <= bbox.max.y;
}

/**
 * Capture complete camera state for later restoration
 * Stores all matrices and parameters needed to recreate exact view
 *
 * @param {THREE.Camera} camera - Active camera
 * @returns {Object} Serializable camera state
 */
export function captureCameraState(camera) {
  return {
    // Position and rotation
    position: camera.position.toArray(),
    rotation: camera.rotation.toArray(),
    quaternion: camera.quaternion.toArray(),

    // Matrices (critical for exact projection)
    projectionMatrix: camera.projectionMatrix.toArray(),
    matrixWorld: camera.matrixWorld.toArray(),
    matrixWorldInverse: camera.matrixWorldInverse.toArray(),

    // Camera parameters
    fov: camera.fov,
    aspect: camera.aspect,
    near: camera.near,
    far: camera.far,
    zoom: camera.zoom,

    // Metadata
    type: camera.type,  // 'PerspectiveCamera' or 'OrthographicCamera'
    timestamp: Date.now()
  };
}

/**
 * Restore camera from saved state
 * Creates new camera instance with identical view parameters
 *
 * @param {Object} state - Saved camera state
 * @returns {THREE.Camera} Restored camera instance
 */
export function restoreCameraState(state) {
  // Create camera of correct type
  let camera;

  if (state.type === 'OrthographicCamera') {
    // Orthographic camera (less common for point clouds)
    camera = new THREE.OrthographicCamera(-1, 1, 1, -1, state.near, state.far);
  } else {
    // Perspective camera (default)
    camera = new THREE.PerspectiveCamera(
      state.fov,
      state.aspect,
      state.near,
      state.far
    );
  }

  // Restore position and rotation
  camera.position.fromArray(state.position);
  camera.rotation.fromArray(state.rotation);
  camera.quaternion.fromArray(state.quaternion);

  // Restore matrices (critical for exact projection)
  camera.projectionMatrix.fromArray(state.projectionMatrix);
  camera.matrixWorld.fromArray(state.matrixWorld);
  camera.matrixWorldInverse.fromArray(state.matrixWorldInverse);

  // Restore zoom
  camera.zoom = state.zoom;

  return camera;
}

/**
 * Calculate polygon area in screen space
 * Uses shoelace formula
 *
 * @param {Array<{x: number, y: number}>} polygon - Polygon vertices
 * @returns {number} Area in square pixels
 */
export function calculatePolygonArea(polygon) {
  if (!polygon || polygon.length < 3) {
    return 0;
  }

  let area = 0;

  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    area += polygon[i].x * polygon[j].y;
    area -= polygon[j].x * polygon[i].y;
  }

  return Math.abs(area / 2);
}

/**
 * Calculate polygon centroid
 *
 * @param {Array<{x: number, y: number}>} polygon - Polygon vertices
 * @returns {{x: number, y: number}} Centroid position
 */
export function calculatePolygonCentroid(polygon) {
  if (!polygon || polygon.length === 0) {
    return { x: 0, y: 0 };
  }

  let cx = 0;
  let cy = 0;

  for (const point of polygon) {
    cx += point.x;
    cy += point.y;
  }

  return {
    x: cx / polygon.length,
    y: cy / polygon.length
  };
}

/**
 * Simplify polygon using Douglas-Peucker algorithm
 * Reduces vertex count while preserving shape
 *
 * @param {Array<{x: number, y: number}>} points - Original polygon vertices
 * @param {number} tolerance - Distance tolerance in pixels
 * @returns {Array<{x: number, y: number}>} Simplified polygon
 */
export function simplifyPolygon(points, tolerance = 2.0) {
  if (!points || points.length <= 3) {
    return points;
  }

  return douglasPeucker(points, tolerance);
}

/**
 * Douglas-Peucker line simplification algorithm
 *
 * @param {Array<{x, y}>} points - Points to simplify
 * @param {number} tolerance - Distance tolerance
 * @returns {Array<{x, y}>} Simplified points
 */
function douglasPeucker(points, tolerance) {
  if (points.length <= 2) {
    return points;
  }

  // Find point with maximum distance from line
  let maxDistance = 0;
  let maxIndex = 0;
  const start = points[0];
  const end = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], start, end);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  // If max distance exceeds tolerance, recursively simplify
  if (maxDistance > tolerance) {
    const left = douglasPeucker(points.slice(0, maxIndex + 1), tolerance);
    const right = douglasPeucker(points.slice(maxIndex), tolerance);
    return left.slice(0, -1).concat(right);
  } else {
    return [start, end];
  }
}

/**
 * Calculate perpendicular distance from point to line segment
 *
 * @param {{x, y}} point - Point to measure
 * @param {{x, y}} lineStart - Line segment start
 * @param {{x, y}} lineEnd - Line segment end
 * @returns {number} Perpendicular distance
 */
function perpendicularDistance(point, lineStart, lineEnd) {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lineLength = Math.sqrt(dx * dx + dy * dy);

  if (lineLength === 0) {
    // Line has zero length, return distance to start point
    const pdx = point.x - lineStart.x;
    const pdy = point.y - lineStart.y;
    return Math.sqrt(pdx * pdx + pdy * pdy);
  }

  // Calculate perpendicular distance using cross product
  const numerator = Math.abs(
    dy * point.x - dx * point.y + lineEnd.x * lineStart.y - lineEnd.y * lineStart.x
  );

  return numerator / lineLength;
}

/**
 * Check if two polygons intersect
 * Uses separating axis theorem (SAT)
 *
 * @param {Array<{x, y}>} polygon1 - First polygon
 * @param {Array<{x, y}>} polygon2 - Second polygon
 * @returns {boolean} True if polygons intersect
 */
export function doPolygonsIntersect(polygon1, polygon2) {
  // Simplified check: test if any vertices of one polygon are inside the other
  for (const point of polygon1) {
    if (isPointInPolygon(point, polygon2)) {
      return true;
    }
  }

  for (const point of polygon2) {
    if (isPointInPolygon(point, polygon1)) {
      return true;
    }
  }

  return false;
}

/**
 * Convert polygon from screen coordinates to NDC
 * Useful for shader uniforms
 *
 * @param {Array<{x, y}>} polygon - Polygon in screen coordinates
 * @param {HTMLElement} canvas - Renderer canvas
 * @returns {Array<{x, y}>} Polygon in NDC coordinates
 */
export function polygonToNDC(polygon, canvas) {
  return polygon.map(point => screenToNDC(point.x, point.y, canvas));
}

export default {
  screenToNDC,
  worldToScreen,
  screenToRay,
  isPointInPolygon,
  calculatePolygonBoundingBox,
  isInBoundingBox,
  captureCameraState,
  restoreCameraState,
  calculatePolygonArea,
  calculatePolygonCentroid,
  simplifyPolygon,
  doPolygonsIntersect,
  polygonToNDC
};
